import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsDownloadButton,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import {
  useErrValidationFailedContext,
  useWriteValidationError,
} from "@/shared/lib/errors/err-validation-failed-context";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DocumentState } from "@/services/backend/documents/types/state";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { Contract } from "@/services/backend/omni/contract/types/contract";
import {
  useOmniContractPdfDownloadMutation,
  useOmniContractShowQuery,
  useOmniContractUpdateStateMutation,
  useLazyOmniContractPdfPreviewQuery,
} from "@/services/backend/omni/contract/api/contract-api";

export function ContractPdf({ contract }: { contract: Contract }) {
  const previewRequest = { id: contract.id };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyOmniContractPdfPreviewQuery();

  // The number is read from the cache: the caller's copy of the contract may
  // not have picked up an edit of the number yet.
  const { data: cachedContract } = useOmniContractShowQuery({
    id: contract.id,
  });

  const { write } = useErrValidationFailedContext();
  const { user } = useAuth();

  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Vertrag")}
              documentNumber={
                <ReferenceNumber
                  referenceNumber={cachedContract?.referenceNumber}
                />
              }
            />
            <DocumentSheetActionsContent>
              {!isImmutable(contract) && (
                <>
                  <DocumentNotReadyAlert document={t("Vertrag")} />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      `Sobald der Vertrag festgeschrieben ist, wird das Wasserzeichen "Vorschau" entfernt. Sie können den Vertrag jederzeit herunterladen.`,
                    )}
                  />
                  <Separator />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: contract.id,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useOmniContractUpdateStateMutation}
                    description={t(
                      "Der Vertrag wird festgeschrieben. Danach kann der Vertrag nicht mehr geändert werden.",
                    )}
                    onSuccess={() => loadPreview(previewRequest)}
                    onError={write}
                  />
                </>
              )}
              <DocumentSheetActionsDownload>
                <ContractDownloadButton contractId={contract.id} />
                <ContractDataSheetsDownloadButton contractId={contract.id} />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function ContractDownloadButton({ contractId }: { contractId: string }) {
  const [download, { isLoading, error, data, reset }] =
    useOmniContractPdfDownloadMutation();

  useDownloadPdf(data, reset);
  useWriteValidationError(error, { onWrite: reset });

  const doDownload = () => {
    if (isLoading) {
      return;
    }
    download({ id: contractId });
  };

  return (
    <>
      <DocumentSheetActionsDownloadButton
        isLoading={isLoading}
        onClick={doDownload}
      >
        {t("Vertrag")}
      </DocumentSheetActionsDownloadButton>
      <RTKQueryErrorAlert error={error} />
    </>
  );
}

// ContractDataSheetsDownloadButton downloads the data sheets of the positions
// of the contract alone; the contract PDF already ends with them.
function ContractDataSheetsDownloadButton({
  contractId,
}: {
  contractId: string;
}) {
  const [download, { isLoading, error, data, reset }] =
    useOmniContractPdfDownloadMutation();

  useDownloadPdf(data, reset);

  const doDownload = () => {
    if (isLoading) {
      return;
    }
    download({ id: contractId, document: "data-sheets" });
  };

  return (
    <>
      <DocumentSheetActionsDownloadButton
        isLoading={isLoading}
        onClick={doDownload}
      >
        {t("Datenblätter")}
      </DocumentSheetActionsDownloadButton>
      <RTKQueryErrorAlert error={error} />
    </>
  );
}
